const STORY_MOBILE_MEDIA_QUERY = window.matchMedia("(max-width: 768px)");

function syncViewportMode() {
  window.isMobile = STORY_MOBILE_MEDIA_QUERY.matches;
  return window.isMobile;
}

syncViewportMode();
gsap.registerPlugin(ScrollTrigger);

function createSection1Reveal({
  imageHandle = "section-1-image",
  textHandle = "section-1-text",
  start = "top top",
  end = () => `+=${window.innerHeight}`,
  scrub = 1,
  pin = true,
}) {
  const section = document.querySelector(`[handle="section-1"]`);
  const image = section?.querySelector(`[handle="${imageHandle}"]`);
  const imageVisual =
    image?.querySelector("img") || image?.querySelector("picture");
  const text = section?.querySelector(`[handle="${textHandle}"]`);

  if (!section || !image || !imageVisual || !text) {
    return;
  }

  const setInitialState = () => {
    gsap.set(imageVisual, {
      scale: 1.2,
      transformOrigin: "center top",
    });
    gsap.set(text, {
      opacity: 0,
      y: window.isMobile ? 48 : 72,
    });
  };

  setInitialState();

  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: section,
      start,
      end,
      scrub,
      pin,
      anticipatePin: pin ? 1 : 0,
      invalidateOnRefresh: true,
      onRefreshInit: setInitialState,
    },
  });

  tl.to(
    imageVisual,
    {
      scale: 1,
      ease: "none",
    },
    0,
  ).to(
    text,
    {
      opacity: 1,
      y: 0,
      ease: "none",
    },
    0.35,
  );
}

function createImageRevealSection({
  sectionHandle,
  containerHandle,
  imageHandle,
  swapImageHandle = null,
  contentHandle,
  itemHandles,
  contentPosition,
  imageEndWidth = "100vw",
  overlayStart = 0.14,
  overlayEnd = 0.32,
  initialBorderRadius = "24px",
  finalBorderRadius = "0px",
  contentEndWidth = "100vw",
  contentEndBottom,
  stagedTitleFirst = false,
  centerTitleBeforeRest = false,
  collapseTitleAfterReveal = true,
  pinEnd = "+=140%",
  animateContent = true,
  animateContentContainer = true,
  separateContentTimeline = false,
  imagePhaseDuration = 0.5,
  contentStartAt = 0,
  contentLeadIn = 0.18,
  animateImagePhase = true,
}) {
  const section = document.querySelector(`[handle="${sectionHandle}"]`);
  const container = document.querySelector(`[handle="${containerHandle}"]`);
  const image = document.querySelector(`[handle="${imageHandle}"]`);
  const swapImage = swapImageHandle
    ? section?.querySelector(`[handle="${swapImageHandle}"]`)
    : null;
  const content = document.querySelector(`[handle="${contentHandle}"]`);

  if (!section || !container || !image || !content) {
    return;
  }

  const items = itemHandles
    .map((handle) => section.querySelector(`[handle="${handle}"]`))
    .filter(Boolean);

  if (!items.length) {
    return;
  }

  const [titleItem, ...restItems] = items;
  const shouldStageTitleInCenter =
    animateContent && stagedTitleFirst && titleItem && centerTitleBeforeRest;
  const shouldAnchorTitleToImageCenter =
    shouldStageTitleInCenter && !animateContentContainer;
  const shouldCollapseStagedTitle =
    shouldAnchorTitleToImageCenter && collapseTitleAfterReveal;

  const overlayVar =
    imageHandle === "section-5-image"
      ? "--section-5-overlay-opacity"
      : "--section-2-overlay-opacity";
  const resetImageSwapState = () => {
    if (!swapImage) {
      return;
    }

    gsap.set(image, { autoAlpha: 1 });
    gsap.set(swapImage, {
      autoAlpha: 0,
      borderRadius: finalBorderRadius,
      [overlayVar]: overlayEnd,
    });
  };

  const getCenteredOffsets = (
    element,
    { axis = "both", targetElement = null } = {},
  ) => {
    if (!element) {
      return { x: 0, y: 0 };
    }

    const rect = element.getBoundingClientRect();
    const targetRect = targetElement?.getBoundingClientRect();
    const targetCenterX = targetRect
      ? targetRect.left + targetRect.width / 2
      : window.innerWidth / 2;
    const targetCenterY = targetRect
      ? targetRect.top + targetRect.height / 2
      : window.innerHeight / 2;

    return {
      x: axis === "y" ? 0 : targetCenterX - (rect.left + rect.width / 2),
      y: axis === "x" ? 0 : targetCenterY - (rect.top + rect.height / 2),
    };
  };

  const getTitleCenteredOffsets = () =>
    shouldAnchorTitleToImageCenter
      ? getCenteredOffsets(titleItem, {
          axis: "y",
          targetElement: image,
        })
      : getCenteredOffsets(titleItem);

  const getDesktopState = () => {
    const imageRect = image.getBoundingClientRect();
    const contentRect = content.getBoundingClientRect();
    const containerStyle = window.getComputedStyle(container);
    const sectionStyle = window.getComputedStyle(section);
    const contentStyle = window.getComputedStyle(content);

    return {
      imageWidth: `${imageRect.width}px`,
      imageHeight: `${imageRect.height}px`,
      contentWidth: `${contentRect.width}px`,
      containerPaddingLeft: containerStyle.paddingLeft,
      containerPaddingRight: containerStyle.paddingRight,
      sectionPaddingTop: sectionStyle.paddingTop,
      sectionPaddingBottom: sectionStyle.paddingBottom,
      sectionPaddingLeft: sectionStyle.paddingLeft,
      sectionPaddingRight: sectionStyle.paddingRight,
      contentRight: contentStyle.right,
      contentBottom: contentStyle.bottom,
      contentLeft: contentStyle.left,
    };
  };

  let desktopState = null;

  const applyDesktopState = () => {
    desktopState = getDesktopState();

    gsap.set(image, {
      width: desktopState.imageWidth,
      height: desktopState.imageHeight,
    });
    if (animateContentContainer) {
      gsap.set(content, {
        width: desktopState.contentWidth,
      });
    } else {
      gsap.set(content, {
        clearProps: "width,left,right,bottom,xPercent",
      });
    }
    gsap.set(section, {
      paddingTop: desktopState.sectionPaddingTop,
      paddingBottom: desktopState.sectionPaddingBottom,
      paddingLeft: desktopState.sectionPaddingLeft,
      paddingRight: desktopState.sectionPaddingRight,
    });
    gsap.set(container, {
      paddingLeft: desktopState.containerPaddingLeft,
      paddingRight: desktopState.containerPaddingRight,
    });

    if (!animateContentContainer) {
      return;
    }

    if (contentPosition === "center-bottom") {
      gsap.set(content, {
        left: "50%",
        xPercent: -50,
        bottom: desktopState.contentBottom,
      });
    } else {
      gsap.set(content, {
        right: desktopState.contentRight,
        bottom: desktopState.contentBottom,
        left: "auto",
        xPercent: 0,
      });
    }
  };

  const applyTitleBoxState = () => {
    if (!shouldCollapseStagedTitle) {
      return;
    }

    gsap.set(titleItem, {
      height: titleItem.offsetHeight,
      overflow: "hidden",
    });
  };

  const setInitialAnimationState = () => {
    if (shouldAnchorTitleToImageCenter) {
      gsap.set(titleItem, {
        clearProps:
          "height,marginTop,marginBottom,paddingTop,paddingBottom,overflow",
      });
      if (shouldCollapseStagedTitle) {
        applyTitleBoxState();
      }
    }

    gsap.set(content, { opacity: animateContentContainer ? 0 : 1 });

    if (!animateContent) {
      gsap.set(items, { opacity: 1, x: 0, y: 0 });
      return;
    }

    if (shouldStageTitleInCenter) {
      const centeredOffsets = getTitleCenteredOffsets();

      gsap.set(titleItem, {
        opacity: 0,
        x: centeredOffsets.x,
        y: centeredOffsets.y,
      });
      gsap.set(restItems, { opacity: 0, y: 56 });
      return;
    }

    gsap.set(items, { opacity: 0, x: 0, y: 56 });
  };

  applyDesktopState();
  gsap.set(image, {
    borderRadius: initialBorderRadius,
    [overlayVar]: overlayStart,
  });
  resetImageSwapState();
  setInitialAnimationState();

  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: section,
      start: "top top",
      end: pinEnd,
      scrub: 1,
      pin: true,
      anticipatePin: 1,
      invalidateOnRefresh: true,
      onRefreshInit: () => {
        gsap.set(image, { clearProps: "width,height" });
        gsap.set(content, { clearProps: "width,left,right,bottom,xPercent" });
        gsap.set(container, { clearProps: "paddingLeft,paddingRight" });
        gsap.set(section, {
          clearProps: "paddingTop,paddingBottom,paddingLeft,paddingRight",
        });
        applyDesktopState();
        gsap.set(image, {
          borderRadius: initialBorderRadius,
          [overlayVar]: overlayStart,
        });
        resetImageSwapState();
        setInitialAnimationState();
      },
    },
  });
  const contentTl = separateContentTimeline
    ? gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: "top top",
          end: pinEnd,
          scrub: 1,
          invalidateOnRefresh: true,
        },
      })
    : tl;
  const atContent = (position = 0) => contentStartAt + position;
  const contentFadeStart = Math.max(0, contentLeadIn - 0.04);
  const titleRevealStart = contentLeadIn;
  const titleCollapseStart = contentLeadIn + 0.28;
  const restItemsStart =
    contentLeadIn + (shouldCollapseStagedTitle ? 0.46 : 0.2);
  const itemRevealBaseStart = contentLeadIn + 0.04;

  if (animateImagePhase) {
    tl.fromTo(
      image,
      {
        width: () => desktopState.imageWidth,
      },
      {
        width: imageEndWidth,
        duration: imagePhaseDuration,
        ease: "none",
      },
      0,
    )
      .fromTo(
        image,
        {
          height: () => desktopState.imageHeight,
        },
        {
          height: "100vh",
          duration: imagePhaseDuration,
          ease: "none",
        },
        0,
      )
      .to(
        section,
        {
          paddingTop: "0px",
          paddingBottom: "0px",
          paddingLeft: "0px",
          paddingRight: "0px",
          duration: imagePhaseDuration,
          ease: "none",
        },
        0,
      )
      .to(
        container,
        {
          paddingLeft: "0px",
          paddingRight: "0px",
          duration: imagePhaseDuration,
          ease: "none",
        },
        0,
      )
      .fromTo(
        image,
        {
          borderRadius: initialBorderRadius,
          [overlayVar]: overlayStart,
        },
        {
          borderRadius: finalBorderRadius,
          [overlayVar]: overlayEnd,
          duration: imagePhaseDuration,
          ease: "none",
        },
        0,
      );
  }

  if (animateImagePhase && swapImage) {
    tl.to(
      image,
      {
        autoAlpha: 0,
        duration: 0.01,
        ease: "none",
      },
      imagePhaseDuration,
    ).to(
      swapImage,
      {
        autoAlpha: 1,
        duration: 0.01,
        ease: "none",
      },
      imagePhaseDuration,
    );
  }

  if (animateContentContainer) {
    contentTl.fromTo(
      content,
      {
        width: () => desktopState.contentWidth,
      },
      {
        width: contentEndWidth,
        ease: "none",
      },
      atContent(0),
    );
  }

  if (animateContentContainer && contentPosition === "center-bottom") {
    contentTl.fromTo(
      content,
      {
        left: "50%",
        xPercent: -50,
        bottom: () => desktopState.contentBottom,
      },
      {
        left: "50%",
        xPercent: -50,
        bottom: contentEndBottom || "80px",
        ease: "none",
      },
      atContent(0),
    );
  } else if (animateContentContainer) {
    contentTl.fromTo(
      content,
      {
        right: () => desktopState.contentRight,
        bottom: () => desktopState.contentBottom,
        xPercent: 0,
      },
      {
        right: "80px",
        bottom: contentEndBottom || "100px",
        xPercent: 0,
        ease: "none",
      },
      atContent(0),
    );
  }

  if (!animateContent) {
    return;
  }

  if (stagedTitleFirst && titleItem) {
    if (centerTitleBeforeRest) {
      contentTl.fromTo(
        titleItem,
        {
          opacity: 0,
          x: () => getTitleCenteredOffsets().x,
          y: () => (animateContentContainer ? 56 : getTitleCenteredOffsets().y),
        },
        {
          opacity: 1,
          x: 0,
          y: 0,
          duration: 0.18,
          ease: "none",
        },
        atContent(titleRevealStart),
      );
    }

    if (animateContentContainer) {
      contentTl.to(
        content,
        {
          opacity: 1,
          duration: 0.2,
          ease: "none",
        },
        atContent(contentFadeStart),
      );
    }

    if (shouldCollapseStagedTitle) {
      contentTl.to(
        titleItem,
        {
          opacity: 0,
          y: -24,
          height: 0,
          marginTop: 0,
          marginBottom: 0,
          paddingTop: 0,
          paddingBottom: 0,
          duration: 0.18,
          ease: "none",
        },
        atContent(titleCollapseStart),
      );
    }

    contentTl.to(
      restItems,
      {
        opacity: 1,
        y: 0,
        duration: 0.18,
        stagger: 0.12,
        ease: "none",
      },
      atContent(restItemsStart),
    );
  } else {
    if (animateContentContainer) {
      contentTl.to(
        content,
        {
          opacity: 1,
          duration: 0.28,
          ease: "none",
        },
        atContent(contentLeadIn),
      );
    }

    items.forEach((item, index) => {
      contentTl.to(
        item,
        {
          opacity: 1,
          y: 0,
          duration: 0.18,
          ease: "none",
        },
        atContent(itemRevealBaseStart + index * 0.12),
      );
    });
  }
}

function createSection2Reveal({
  pinEnd = window.isMobile ? "+=200%" : "+=180%",
  imageEndWidth = "100vw",
  overlayStart = 0.14,
  overlayEnd = 0.32,
  primaryContentStart = 0.54,
  primaryItemStart = primaryContentStart,
  primaryItemStagger = 0,
  swapStart = 0.84,
  secondaryContentStart = 1.04,
  secondaryItemStart = secondaryContentStart,
  secondaryItemStagger = 0,
  initialImageScale = window.isMobile ? 1.2 : 1,
  imageScaleStart = 0,
  imageScaleDuration = 0.48,
  swapImageOffsetYPercent = 0,
  swapImageVisualOffsetYPercent = window.isMobile ? 6 : 3,
  swapImageInitialScale = window.isMobile ? 1.06 : 1.04,
  swapImageStartOpacityThreshold = 0.45,
  swapImageRevealDuration = window.isMobile ? 0.22 : 0.16,
}) {
  const section = document.querySelector(`[handle="section-2"]`);
  const container = section?.querySelector(`[handle="section-2-container"]`);
  const image = section?.querySelector(`[handle="section-2-image"]`);
  const swapImage = section?.querySelector(`[handle="section-2-image-2"]`);
  const content = section?.querySelector(`[handle="section-2-content"]`);
  const secondaryContent = section?.querySelector(
    `[handle="section-2-content-2"]`,
  );
  const imageVisual =
    image?.querySelector("picture") || image?.querySelector("img");
  const swapImageVisual =
    swapImage?.querySelector("picture") || swapImage?.querySelector("img");

  if (
    !section ||
    !container ||
    !image ||
    !swapImage ||
    !content ||
    !secondaryContent
  ) {
    return;
  }

  const getContentItems = (root) =>
    ["text", "container", "sub-text"]
      .map((handle) => root.querySelector(`[handle="${handle}"]`))
      .filter(
        (item) => item && window.getComputedStyle(item).display !== "none",
      );

  const primaryItems = getContentItems(content);
  const secondaryItems = getContentItems(secondaryContent);
  const animatedItems = [...primaryItems, ...secondaryItems];

  if (!primaryItems.length || !secondaryItems.length) {
    return;
  }

  const getLayoutState = () => {
    const imageRect = image.getBoundingClientRect();
    const containerStyle = window.getComputedStyle(container);
    const sectionStyle = window.getComputedStyle(section);

    return {
      imageWidth: `${imageRect.width}px`,
      imageHeight: `${imageRect.height}px`,
      containerPaddingLeft: containerStyle.paddingLeft,
      containerPaddingRight: containerStyle.paddingRight,
      sectionPaddingTop: sectionStyle.paddingTop,
      sectionPaddingBottom: sectionStyle.paddingBottom,
      sectionPaddingLeft: sectionStyle.paddingLeft,
      sectionPaddingRight: sectionStyle.paddingRight,
    };
  };

  let layoutState = null;

  const adjustMobilePinSpacer = (scrollTrigger) => {
    const pinSpacer = scrollTrigger.pin?.parentNode;
    if (!(pinSpacer instanceof HTMLElement)) {
      return;
    }

    if (!window.isMobile) {
      pinSpacer.style.marginBottom = "";
      return;
    }

    const sectionPaddingBottom =
      parseFloat(layoutState?.sectionPaddingBottom || "0") || 0;

    if (!sectionPaddingBottom) {
      pinSpacer.style.marginBottom = "";
      return;
    }

    pinSpacer.style.marginBottom = `-${sectionPaddingBottom}px`;
  };

  const setContentInitialState = (targetContent, items) => {
    gsap.set(targetContent, { autoAlpha: 0 });
    gsap.set(items, {
      autoAlpha: 0,
      y: window.isMobile ? 40 : 56,
    });
  };

  const applyLayoutState = () => {
    layoutState = getLayoutState();
    const sectionPaddingTop = window.isMobile
      ? "30px"
      : layoutState.sectionPaddingTop;
    const sectionPaddingBottom = window.isMobile
      ? "30px"
      : layoutState.sectionPaddingBottom;

    gsap.set(image, {
      width: layoutState.imageWidth,
      height: layoutState.imageHeight,
    });
    gsap.set(section, {
      paddingTop: sectionPaddingTop,
      paddingBottom: sectionPaddingBottom,
      paddingLeft: layoutState.sectionPaddingLeft,
      paddingRight: layoutState.sectionPaddingRight,
    });
    gsap.set(container, {
      paddingLeft: layoutState.containerPaddingLeft,
      paddingRight: layoutState.containerPaddingRight,
    });
  };

  const setInitialState = () => {
    applyLayoutState();

    gsap.set(image, {
      autoAlpha: 1,
      borderRadius: "0px",
      "--section-2-overlay-opacity": overlayStart,
    });
    if (imageVisual) {
      gsap.set(imageVisual, {
        scale: initialImageScale,
        transformOrigin: "center center",
      });
    }
    gsap.set(swapImage, {
      autoAlpha: 0,
      yPercent: swapImageOffsetYPercent,
      borderRadius: "0px",
      "--section-2-overlay-opacity": overlayEnd,
    });
    if (swapImageVisual) {
      gsap.set(swapImageVisual, {
        scale: swapImageInitialScale,
        yPercent: swapImageVisualOffsetYPercent,
        transformOrigin: "center center",
      });
    }
    setContentInitialState(content, primaryItems);
    setContentInitialState(secondaryContent, secondaryItems);
  };

  setInitialState();
  const clampedSwapImageStartOpacityThreshold = Math.min(
    Math.max(swapImageStartOpacityThreshold, 0),
    1,
  );
  const swapImageFadeInStart =
    swapStart +
    swapImageRevealDuration * (1 - clampedSwapImageStartOpacityThreshold);

  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: section,
      start: "top top",
      end: pinEnd,
      scrub: 1,
      pin: true,
      anticipatePin: 1,
      invalidateOnRefresh: true,
      onRefreshInit: () => {
        gsap.set(image, {
          clearProps: "width,height,opacity,visibility",
        });
        gsap.set(swapImage, {
          clearProps: "opacity,visibility,transform",
        });
        gsap.set(section, {
          clearProps: "paddingTop,paddingBottom,paddingLeft,paddingRight",
        });
        gsap.set(container, {
          clearProps: "paddingLeft,paddingRight",
        });
        const visuals = [imageVisual, swapImageVisual].filter(Boolean);
        if (visuals.length) {
          gsap.set(visuals, {
            clearProps: "transform,transformOrigin",
          });
        }
        gsap.set([content, secondaryContent], {
          clearProps: "opacity,visibility",
        });
        gsap.set(animatedItems, {
          clearProps: "opacity,visibility,transform",
        });
        setInitialState();
      },
      onRefresh: (self) => {
        adjustMobilePinSpacer(self);
      },
    },
  });

  tl.fromTo(
    image,
    {
      width: () => layoutState.imageWidth,
    },
    {
      width: imageEndWidth,
      duration: 0.48,
      ease: "none",
    },
    0,
  )
    .fromTo(
      image,
      {
        height: () => layoutState.imageHeight,
      },
      {
        height: () =>
          window.isMobile
            ? `calc(100vh - ${layoutState.sectionPaddingTop} - ${layoutState.sectionPaddingBottom})`
            : "100vh",
        duration: 0.48,
        ease: "none",
      },
      0,
    )
    .to(
      section,
      {
        paddingTop: () =>
          window.isMobile ? layoutState.sectionPaddingTop : "0px",
        paddingBottom: () =>
          window.isMobile ? layoutState.sectionPaddingBottom : "0px",
        paddingLeft: "0px",
        paddingRight: "0px",
        duration: 0.48,
        ease: "none",
      },
      0,
    )
    .to(
      container,
      {
        paddingLeft: "0px",
        paddingRight: "0px",
        duration: 0.48,
        ease: "none",
      },
      0,
    )
    .fromTo(
      image,
      {
        "--section-2-overlay-opacity": overlayStart,
      },
      {
        "--section-2-overlay-opacity": overlayEnd,
        duration: 0.48,
        ease: "none",
      },
      0,
    )
    .to(
      content,
      {
        autoAlpha: 1,
        duration: 0.08,
        ease: "none",
      },
      primaryContentStart,
    );

  if (initialImageScale !== 1 && imageVisual) {
    tl.to(
      imageVisual,
      {
        scale: 1,
        duration: imageScaleDuration,
        ease: "none",
      },
      imageScaleStart,
    );
  }

  primaryItems.forEach((item, index) => {
    tl.to(
      item,
      {
        autoAlpha: 1,
        y: 0,
        duration: 0.14,
        ease: "none",
      },
      primaryItemStart + index * primaryItemStagger,
    );
  });

  tl.to(
    image,
    {
      autoAlpha: 0,
      duration: swapImageRevealDuration,
      ease: "none",
    },
    swapStart,
  )
    .to(
      content,
      {
        autoAlpha: 0,
        duration: swapImageRevealDuration,
        ease: "none",
      },
      swapStart,
    )
    .fromTo(
      swapImage,
      {
        autoAlpha: 0,
        yPercent: swapImageOffsetYPercent,
      },
      {
        autoAlpha: 1,
        yPercent: 0,
        duration: swapImageRevealDuration,
        ease: "none",
      },
      swapImageFadeInStart,
    )
    .to(
      swapImageVisual,
      {
        scale: 1,
        yPercent: 0,
        duration: swapImageRevealDuration,
        ease: "none",
      },
      swapImageFadeInStart,
    )
    .to(
      secondaryContent,
      {
        autoAlpha: 1,
        duration: 0.08,
        ease: "none",
      },
      secondaryContentStart,
    );

  secondaryItems.forEach((item, index) => {
    tl.to(
      item,
      {
        autoAlpha: 1,
        y: 0,
        duration: 0.14,
        ease: "none",
      },
      secondaryItemStart + index * secondaryItemStagger,
    );
  });
}

function createSection3Reveal({
  visualHandle,
  start = window.isMobile ? "top 78%" : "top 72%",
  end = window.isMobile ? "+=220px" : "+=260px",
  scrub = 1,
  contentOffsetY = window.isMobile ? 40 : 56,
  visualOffsetY = window.isMobile ? 48 : 64,
}) {
  const section = document.querySelector(`[handle="section-3"]`);
  const content = section?.querySelector(`[handle="section-3-content"]`);
  const visual = section?.querySelector(`[handle="${visualHandle}"]`);

  if (!section || !content || !visual) {
    return;
  }

  const setInitialState = () => {
    gsap.set(content, {
      autoAlpha: 0,
      y: contentOffsetY,
    });
    gsap.set(visual, {
      autoAlpha: 0,
      y: visualOffsetY,
    });
  };

  setInitialState();

  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: section,
      start,
      end,
      scrub,
      invalidateOnRefresh: true,
      onRefreshInit: setInitialState,
    },
  });

  tl.to(
    content,
    {
      autoAlpha: 1,
      y: 0,
      ease: "none",
      duration: 0.28,
    },
    0,
  ).to(
    visual,
    {
      autoAlpha: 1,
      y: 0,
      ease: "none",
      duration: 0.28,
    },
    0.32,
  );
}

function setupSection3PcBackgroundSwap() {
  const SECTION_3_GIF_PLAYBACK_MS = 3000;
  const section = document.querySelector(`[handle="section-3"]`);
  const imageContainer = section?.querySelector(
    `[handle="section-3-image-container"]`,
  );
  const items = imageContainer
    ? Array.from(
        imageContainer.querySelectorAll(`[handle="section-3-image-item"]`),
      )
    : [];
  const backgroundImages = [
    "section-3-pc-image-1",
    "section-3-pc-image-2",
    "section-3-pc-image-3",
  ]
    .map((handle) => section?.querySelector(`[handle="${handle}"]`))
    .filter(Boolean);
  const gifBackgroundImages = imageContainer
    ? Array.from(
        imageContainer.querySelectorAll(`[handle="section-3-gif-image-item"]`),
      ).filter((gifItem) => !items.some((item) => item.contains(gifItem)))
    : [];

  if (
    !section ||
    !imageContainer ||
    !items.length ||
    !backgroundImages.length
  ) {
    return;
  }

  if (typeof imageContainer.__section3BgSwapCleanup === "function") {
    imageContainer.__section3BgSwapCleanup();
  }

  const itemImagePairs = items
    .map((item, index) => ({
      item,
      backgroundImage: backgroundImages[index],
      gifBackgroundImage:
        item.querySelector(`[handle="section-3-gif-image-item"]`) ||
        gifBackgroundImages[index] ||
        null,
    }))
    .filter(({ backgroundImage }) => backgroundImage);

  if (!itemImagePairs.length) {
    return;
  }

  let activeGifCleanup = null;
  let activeGifPair = null;
  const gifPreloadMap = new Map();

  const hideBackgroundImages = ({ animate = false } = {}) => {
    if (!animate) {
      gsap.set(backgroundImages, { autoAlpha: 0 });
      return;
    }

    gsap.to(backgroundImages, {
      autoAlpha: 0,
      duration: 0.24,
      ease: "power2.out",
      overwrite: true,
      stagger: 0,
    });
  };

  const showBackgroundImage = (targetImage) => {
    backgroundImages.forEach((backgroundImage) => {
      gsap.to(backgroundImage, {
        autoAlpha: targetImage && backgroundImage === targetImage ? 1 : 0,
        duration: 0.28,
        ease: "power2.out",
        overwrite: true,
      });
    });
  };

  const clearItemGifBackground = (item) => {
    item.style.removeProperty("--section-3-active-bg-image");
  };

  const parseCssUrl = (value) => {
    const match = value?.match(/url\((['"]?)(.*?)\1\)/);
    return match?.[2] || "";
  };

  const getUrlFromSrcset = (srcset) => {
    const firstEntry = srcset?.split(",")[0]?.trim();
    return firstEntry ? firstEntry.split(/\s+/)[0] : "";
  };

  const getGifBackgroundUrl = (gifBackgroundImage) => {
    if (!gifBackgroundImage) {
      return "";
    }

    const desktopSource = gifBackgroundImage.querySelector(
      'source[media="(min-width: 768px)"]',
    );
    const gifVisual = gifBackgroundImage.querySelector("img");
    const sourceUrl = getUrlFromSrcset(desktopSource?.srcset);
    if (sourceUrl) {
      return sourceUrl;
    }

    const imageUrl =
      gifVisual?.currentSrc || gifVisual?.src || gifVisual?.dataset?.src || "";
    if (imageUrl) {
      return imageUrl;
    }

    const inlineVarUrl = parseCssUrl(
      gifBackgroundImage.style.getPropertyValue("--bg-image-pc"),
    );
    if (inlineVarUrl) {
      return inlineVarUrl;
    }

    const computedStyle = window.getComputedStyle(gifBackgroundImage);
    return (
      parseCssUrl(computedStyle.getPropertyValue("--bg-image-pc")) ||
      parseCssUrl(computedStyle.backgroundImage)
    );
  };

  const ensureGifPreload = (url) => {
    if (!url) {
      return null;
    }

    if (!gifPreloadMap.has(url)) {
      const preloadImage = new Image();
      preloadImage.decoding = "async";
      preloadImage.src = url;
      gifPreloadMap.set(url, preloadImage);
    }

    return gifPreloadMap.get(url);
  };

  const clearActiveGif = () => {
    activeGifCleanup?.();
    activeGifCleanup = null;
    activeGifPair = null;
  };

  const playItemGifBackground = (pair) => {
    const { item, gifBackgroundImage } = pair;
    if (!gifBackgroundImage) {
      return;
    }

    const gifUrl = getGifBackgroundUrl(gifBackgroundImage);
    if (!gifUrl) {
      return;
    }

    ensureGifPreload(gifUrl);
    clearActiveGif();
    let playbackTimerId = null;
    let isCleanedUp = false;

    const cleanup = ({ resetItem = true } = {}) => {
      if (isCleanedUp) {
        return;
      }

      isCleanedUp = true;
      if (playbackTimerId) {
        window.clearTimeout(playbackTimerId);
      }

      if (resetItem) {
        clearItemGifBackground(item);
      }
      if (activeGifCleanup === cleanup) {
        activeGifCleanup = null;
      }
      if (activeGifPair === pair) {
        activeGifPair = null;
      }
    };

    activeGifPair = pair;
    activeGifCleanup = cleanup;

    clearItemGifBackground(item);
    void item.offsetWidth;
    item.style.setProperty("--section-3-active-bg-image", `url("${gifUrl}")`);

    playbackTimerId = window.setTimeout(() => {
      if (activeGifPair === pair) {
        clearItemGifBackground(item);
      }
      cleanup({ resetItem: false });
    }, SECTION_3_GIF_PLAYBACK_MS);
  };

  itemImagePairs.forEach(({ gifBackgroundImage }) => {
    const gifUrl = getGifBackgroundUrl(gifBackgroundImage);
    if (gifUrl) {
      ensureGifPreload(gifUrl);
    }
  });

  hideBackgroundImages();

  const removeListeners = [];
  const addListener = (element, eventName, handler) => {
    element.addEventListener(eventName, handler);
    removeListeners.push(() => {
      element.removeEventListener(eventName, handler);
    });
  };

  itemImagePairs.forEach((pair) => {
    const { item, backgroundImage } = pair;
    const handleMouseEnter = () => {
      showBackgroundImage(backgroundImage);
      playItemGifBackground(pair);
    };
    const handleMouseLeave = () => {
      if (activeGifPair === pair) {
        clearActiveGif();
      }
    };

    addListener(item, "mouseenter", handleMouseEnter);
    addListener(item, "mouseleave", handleMouseLeave);
  });

  const handleContainerMouseLeave = () => {
    clearActiveGif();
    hideBackgroundImages({ animate: true });
  };

  addListener(imageContainer, "mouseleave", handleContainerMouseLeave);

  const cleanup = () => {
    clearActiveGif();
    removeListeners.splice(0).forEach((removeListener) => {
      removeListener();
    });
    backgroundImages.forEach((backgroundImage) => {
      gsap.killTweensOf(backgroundImage);
    });
    itemImagePairs.forEach(({ item }) => {
      clearItemGifBackground(item);
    });
    gsap.set(backgroundImages, {
      clearProps: "opacity,visibility",
    });
    delete imageContainer.__section3BgSwapCleanup;
  };

  imageContainer.__section3BgSwapCleanup = cleanup;

  return cleanup;
}

function section2Pc() {
  createSection2Reveal({
    pinEnd: "+=220%",
    primaryItemStart: 0.54,
    primaryItemStagger: 0,
    swapStart: 1.28,
    secondaryContentStart: 1.48,
    secondaryItemStart: 1.48,
    secondaryItemStagger: 0,
  });
}

function section2Mb() {
  const section = document.querySelector(`[handle="section-2"]`);
  const container =
    section?.querySelector(`[handle="section-2-container-mb"]`) ||
    section?.querySelector(`[handle="section-2-mb-container"]`);
  const imageOne = container?.querySelector(`[handle="section-2-mb-image-1"]`);
  const imageTwo =
    container?.querySelector(`[handle="section-2-mb-image-2"]`) ||
    container?.querySelector(`:scope > [handle="image"]`);
  const contentOne = container?.querySelector(`[handle="section-2-mb-content"]`);
  const contentTwo = container?.querySelector(`[handle="section-2-mb-content-2"]`);
  const imageOneVisual =
    imageOne?.querySelector("img") || imageOne?.querySelector("picture");
  const imageTwoVisual =
    imageTwo?.querySelector("img") || imageTwo?.querySelector("picture");
  const contentOneItems = contentOne
    ? ["text", "sub-text"]
        .map((handle) => contentOne.querySelector(`[handle="${handle}"]`))
        .filter(Boolean)
    : [];
  const contentTwoItems = contentTwo
    ? ["text", "sub-text"]
        .map((handle) => contentTwo.querySelector(`[handle="${handle}"]`))
        .filter(Boolean)
    : [];

  if (
    !section ||
    !container ||
    !imageOne ||
    !imageTwo ||
    !contentOne ||
    !contentTwo ||
    !imageOneVisual ||
    !imageTwoVisual ||
    !contentOneItems.length ||
    !contentTwoItems.length
  ) {
    return;
  }

  let stage = container.querySelector(`[data-section-2-mb-stage]`);
  if (!stage) {
    stage = document.createElement("div");
    stage.setAttribute("data-section-2-mb-stage", "");
    container.insertBefore(stage, imageOne);
  }

  if (imageOne.parentElement !== stage) {
    stage.appendChild(imageOne);
  }
  if (imageTwo.parentElement !== stage) {
    stage.appendChild(imageTwo);
  }
  if (contentOne.parentElement !== imageOne) {
    imageOne.appendChild(contentOne);
  }
  if (contentTwo.parentElement === stage) {
    container.appendChild(contentTwo);
  }

  let contentTwoWrap = container.querySelector(`[data-section-2-mb-content-2-wrap]`);
  if (!contentTwoWrap) {
    contentTwoWrap = document.createElement("div");
    contentTwoWrap.setAttribute("data-section-2-mb-content-2-wrap", "");
    container.insertBefore(contentTwoWrap, contentTwo);
  }
  if (contentTwo.parentElement !== contentTwoWrap) {
    contentTwoWrap.appendChild(contentTwo);
  }

  const getFullHeight = () => imageOne.offsetWidth * (656 / 375);
  const getCollapsedHeight = () => imageTwo.offsetWidth * (316 / 375);
  const getContentTwoLayout = () => {
    return {
      wrapHeight: contentTwo.offsetHeight,
    };
  };

  const setInitialState = () => {
    const fullHeight = getFullHeight();

    gsap.set(container, {
      minHeight: `${fullHeight}px`,
    });
    gsap.set(stage, {
      height: `${fullHeight}px`,
    });
    gsap.set([imageOne, imageTwo], {
      height: `${fullHeight}px`,
      top: 0,
      left: 0,
    });
    gsap.set(imageOne, {
      autoAlpha: 1,
      zIndex: 1,
    });
    gsap.set(imageTwo, {
      width: "100%",
      autoAlpha: 0,
      zIndex: 2,
    });
    gsap.set(imageOneVisual, {
      scale: 1.16,
      transformOrigin: "center center",
    });
    gsap.set(imageTwoVisual, {
      scale: 1,
      objectPosition: "0% center",
      transformOrigin: "center center",
    });
    gsap.set(contentOne, {
      autoAlpha: 0,
      y: 18,
    });
    gsap.set(contentOneItems, {
      autoAlpha: 0,
      y: 18,
    });
    const contentTwoLayout = getContentTwoLayout();
    gsap.set(contentTwoWrap, {
      height: 0,
      overflow: "hidden",
    });
    gsap.set(contentTwo, {
      autoAlpha: 0,
      y: 42,
    });
    gsap.set(contentTwoItems, {
      autoAlpha: 0,
      y: 42,
    });

    return contentTwoLayout;
  };

  let contentTwoLayout = setInitialState();
  const primaryContentHideStart = 0.52;
  const imageSwapStart = 0.6;
  const imageTwoPanStart = 0.6;
  const imageTwoCollapseStart = 0.98;
  const contentTwoRevealStart = 1.14;
  const contentTwoItemsRevealStart = 1.16;

  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: section,
      start: "top top",
      end: "+=210%",
      scrub: 1,
      pin: true,
      anticipatePin: 1,
      invalidateOnRefresh: true,
      onRefreshInit: () => {
        gsap.set(stage, {
          clearProps: "height",
        });
        gsap.set(contentTwoWrap, {
          clearProps: "height,overflow",
        });
        gsap.set([imageOne, imageTwo], {
          clearProps: "height,top,left,opacity,visibility,zIndex",
        });
        gsap.set(container, {
          clearProps: "minHeight",
        });
        gsap.set([contentOne, contentTwo], {
          clearProps: "opacity,visibility,transform",
        });
        gsap.set([...contentOneItems, ...contentTwoItems], {
          clearProps: "opacity,visibility,transform",
        });
        gsap.set([imageOneVisual, imageTwoVisual], {
          clearProps: "transform,transformOrigin,objectPosition",
        });
        contentTwoLayout = setInitialState();
      },
    },
  });

  tl.to(
    imageOneVisual,
    {
      scale: 1,
      duration: 0.28,
      ease: "none",
    },
    0,
  )
    .to(
      contentOne,
      {
        autoAlpha: 1,
        y: 0,
        duration: 0.12,
        ease: "none",
      },
      0.22,
    )
    .to(
      contentOneItems,
      {
        autoAlpha: 1,
        y: 0,
        duration: 0.16,
        stagger: 0.05,
        ease: "none",
      },
      0.24,
    )
    .to(
      contentOne,
      {
        autoAlpha: 0,
        y: -10,
        duration: 0.08,
        ease: "none",
      },
      primaryContentHideStart,
    )
    .set(
      imageTwo,
      {
        autoAlpha: 1,
      },
      imageSwapStart,
    )
    .set(
      imageOne,
      {
        autoAlpha: 0,
      },
      imageSwapStart,
    )
    .to(
      imageTwoVisual,
      {
        objectPosition: "100% center",
        duration: 0.38,
        ease: "none",
      },
      imageTwoPanStart,
    )
    .fromTo(
      imageTwo,
      {
        height: () => `${getFullHeight()}px`,
      },
      {
        height: () => `${getCollapsedHeight()}px`,
        top: 0,
        left: 0,
        duration: 0.18,
        ease: "none",
      },
      imageTwoCollapseStart,
    )
    .to(
      stage,
      {
        height: () => `${getCollapsedHeight()}px`,
        duration: 0.18,
        ease: "none",
      },
      imageTwoCollapseStart,
    )
    .to(
      container,
      {
        minHeight: () => `${getCollapsedHeight() + contentTwoLayout.wrapHeight}px`,
        duration: 0.18,
        ease: "none",
      },
      imageTwoCollapseStart,
    )
    .fromTo(
      contentTwoWrap,
      {
        height: 0,
      },
      {
        height: () => `${contentTwoLayout.wrapHeight}px`,
        duration: 0.14,
        ease: "none",
      },
      contentTwoRevealStart,
    )
    .fromTo(
      contentTwo,
      {
        autoAlpha: 0,
        y: 42,
      },
      {
        autoAlpha: 1,
        y: 0,
        duration: 0.14,
        ease: "none",
      },
      contentTwoRevealStart,
    )
    .to(
      contentTwoItems,
      {
        autoAlpha: 1,
        y: 0,
        duration: 0.12,
        stagger: 0.04,
        ease: "none",
      },
      contentTwoItemsRevealStart,
    );
}

function section1Pc() {
  createSection1Reveal({
    start: "top top",
    end: () => `+=${window.innerHeight}`,
    scrub: 1,
  });
}

function section1Mb() {
  createSection1Reveal({
    start: "top top",
    end: () => `+=${window.innerHeight}`,
    scrub: 1.1,
  });
}

function section3Pc() {
  const cleanupBackgroundSwap = setupSection3PcBackgroundSwap();
  createSection3Reveal({
    visualHandle: "section-3-image-container",
  });

  return cleanupBackgroundSwap;
}

function section3Mb() {
  createSection3Reveal({
    visualHandle: "section-3-embla",
    start: "top 82%",
    end: "+=180px",
    contentOffsetY: 32,
    visualOffsetY: 40,
  });
}

function section5Pc() {
  createImageRevealSection({
    sectionHandle: "section-5",
    containerHandle: "section-5-container",
    imageHandle: "section-5-image",
    contentHandle: "section-5-content",
    itemHandles: [
      "for-the-hunters-title-box",
      "for-the-hunters-sub-title-box",
      "for-the-hunters-notes",
    ],
    contentPosition: "right-bottom",
    imageEndWidth: "100vw",
    initialBorderRadius: "0px",
    finalBorderRadius: "0px",
    contentEndWidth: "40%",
    contentEndBottom: "100px",
    stagedTitleFirst: true,
    centerTitleBeforeRest: true,
    pinEnd: "+=320%",
    animateContent: true,
    animateContentContainer: false,
    separateContentTimeline: false,
    imagePhaseDuration: 1,
    contentStartAt: 0,
    contentLeadIn: 0,
    collapseTitleAfterReveal: false,
    animateImagePhase: false,
  });
}

function section5Mb() {
  const section = document.querySelector(`[handle="section-5"]`);
  const container = section?.querySelector(`[handle="section-5-mb-container"]`);
  const image = container?.querySelector(`[handle="section-5-mb-image"]`);
  const contentItems = [
    container?.querySelector(`[handle="section-5-mb-title"]`),
    container?.querySelector(`[handle="section-5-mb-desc"]`),
    container?.querySelector(`[handle="section-5-mb-subtitle"]`),
  ].filter(Boolean);

  if (!container || !image || !contentItems.length) {
    return;
  }

  const setInitialState = () => {
    gsap.set(image, {
      autoAlpha: 0,
      y: 96,
    });
    gsap.set(contentItems, {
      autoAlpha: 0,
      y: 72,
    });
  };

  setInitialState();

  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: container,
      start: "top 82%",
      toggleActions: "restart none restart reset",
      invalidateOnRefresh: true,
      onRefreshInit: setInitialState,
    },
  });

  tl.to(
    image,
    {
      y: 0,
      autoAlpha: 1,
      duration: 1,
      ease: "power3.out",
    },
    0,
  ).to(
    contentItems,
    {
      y: 0,
      autoAlpha: 1,
      duration: 0.95,
      ease: "power3.out",
      stagger: 0.32,
    },
    0.42,
  );
}

function section7Pc() {
  const emblaNode = document.querySelector(`[handle="section-7"]`);
  if (!emblaNode) {
    return;
  }

  if (typeof emblaNode.__section7PcCleanup === "function") {
    emblaNode.__section7PcCleanup();
  }

  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: `[handle="section-7"]`,
      start: "top 40%",
      end: "+=200px",
      scrub: 1.2,
    },
  });
  tl.from(
    [
      `[handle="section-7-title"]`,
      `[handle="section-7-dots"]`,
      `[handle="section-7-viewport"]`,
    ],
    {
      y: 120,
      opacity: 0,
      duration: 0.5,
      ease: "power3.out",
      stagger: 0.3,
    },
  );

  const viewportNode = emblaNode.querySelector(`[handle="section-7-viewport"]`);
  const nextbtnNode = emblaNode.querySelector(`[handle="section-7-next"]`);
  const prevbtnNode = emblaNode.querySelector(`[handle="section-7-prev"]`);
  const dotsNode = emblaNode.querySelector(`[handle="section-7-dots"]`);

  if (!viewportNode) {
    return;
  }

  const addDotsClickHandlers = (emblaApi, dotsRoot) => {
    if (!dotsRoot) {
      return [];
    }

    const dotNodes = Array.from(dotsRoot.children);
    return dotNodes.map((dotNode, index) => {
      const handleClick = () => {
        emblaApi.goTo(index);
      };
      dotNode.addEventListener("click", handleClick);

      return () => {
        dotNode.removeEventListener("click", handleClick);
      };
    });
  };

  const toggleDotsActive = (emblaApi, dotsRoot) => {
    if (!dotsRoot) {
      return;
    }

    const dotNodes = Array.from(dotsRoot.children);
    dotNodes.forEach((dotNode, index) => {
      dotNode.classList.toggle("active", index === emblaApi.selectedSnap());
    });
  };

  const addPrevNextBtnsClickHandlers = (emblaApi, prevBtn, nextBtn) => {
    const removeListeners = [];

    if (prevBtn) {
      const handlePrevClick = () => {
        emblaApi.goToPrev();
      };
      prevBtn.addEventListener("click", handlePrevClick);
      removeListeners.push(() => {
        prevBtn.removeEventListener("click", handlePrevClick);
      });
    }
    if (nextBtn) {
      const handleNextClick = () => {
        emblaApi.goToNext();
      };
      nextBtn.addEventListener("click", handleNextClick);
      removeListeners.push(() => {
        nextBtn.removeEventListener("click", handleNextClick);
      });
    }

    return removeListeners;
  };

  const toggleArrowsDisabled = (emblaApi, prevBtn, nextBtn) => {
    if (prevBtn) {
      prevBtn.toggleAttribute("data-disabled", !emblaApi.canGoToPrev());
    }
    if (nextBtn) {
      nextBtn.toggleAttribute("data-disabled", !emblaApi.canGoToNext());
    }
  };

  const options = { duration: 30 };
  const plugins = [EmblaCarouselAutoplay({ delay: 3000 }), EmblaCarouselFade()];
  const emblaApi = EmblaCarousel(viewportNode, options, plugins);

  const removeClickListeners = [
    ...addPrevNextBtnsClickHandlers(emblaApi, prevbtnNode, nextbtnNode),
    ...addDotsClickHandlers(emblaApi, dotsNode),
  ];

  toggleArrowsDisabled(emblaApi, prevbtnNode, nextbtnNode);
  toggleDotsActive(emblaApi, dotsNode);

  const handleSelect = () => {
    toggleArrowsDisabled(emblaApi, prevbtnNode, nextbtnNode);
    toggleDotsActive(emblaApi, dotsNode);
  };
  const handleReinit = () => {
    toggleArrowsDisabled(emblaApi, prevbtnNode, nextbtnNode);
    toggleDotsActive(emblaApi, dotsNode);
  };

  emblaApi.on("select", handleSelect);
  emblaApi.on("reinit", handleReinit);

  const cleanup = () => {
    removeClickListeners.forEach((removeListener) => {
      removeListener();
    });
    emblaApi.off?.("select", handleSelect);
    emblaApi.off?.("reinit", handleReinit);
    emblaApi.destroy?.();
    delete emblaNode.__section7PcCleanup;
  };

  emblaNode.__section7PcCleanup = cleanup;

  return cleanup;
}

function section7Mb() {
  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: `[handle="section-7"]`,
      start: "top 50%",
      end: "+=40%",
      scrub: 1.2,
    },
  });

  tl.from(
    [
      `[handle="section-7-title"]`,
      `[handle="section-7-dots"]`,
      `[handle="section-7"] [handle="embla"]`,
    ],
    {
      y: 60,
      opacity: 0,
      duration: 1,
      ease: "power3.out",
      stagger: 0.3,
    },
  );

  const dotsNode = document.querySelector(`[handle="section-7-dots"]`);
  if (!(dotsNode instanceof HTMLElement)) {
    return;
  }

  Array.from(dotsNode.children).forEach((dot) => {
    dot.classList.remove("active");
  });
}

function section8Pc() {
  const section = document.querySelector(`[handle="section-8"]`);
  const subtitle =
    section?.querySelector(`[handle="section-8-subtitle"]`) ||
    section?.querySelector(`[handle="section-8-mb-text"]`);
  const title =
    section?.querySelector(`[handle="section-8-title"]`) ||
    section?.querySelector(`[handle="section-8-pc-title"]`);

  if (!section || !subtitle || !title) {
    return;
  }

  createDirectionalReveal({
    trigger: subtitle,
    items: [
      {
        element: subtitle,
        offsetY: 72,
        duration: 0.95,
        position: 0,
      },
      {
        element: title,
        offsetY: 96,
        duration: 1,
        position: 0.18,
      },
    ],
  });
}

function section8Mb() {
  const section = document.querySelector(`[handle="section-8"]`);
  const text =
    section?.querySelector(`[handle="section-8-subtitle"]`) ||
    section?.querySelector(`[handle="section-8-mb-text"]`);
  const title =
    section?.querySelector(`[handle="section-8-title"]`) ||
    section?.querySelector(`[handle="section-8-mb-title"]`);

  if (!section || !text || !title) {
    return;
  }

  const items = [
    {
      element: text,
      offsetY: 72,
      duration: 0.95,
      position: 0,
    },
    {
      element: title,
      offsetY: 72,
      duration: 0.95,
      position: 0.32,
    },
  ];
  let activeTimeline = null;

  const killActiveTimeline = () => {
    activeTimeline?.kill();
    activeTimeline = null;
    items.forEach(({ element }) => {
      gsap.killTweensOf(element);
    });
  };

  const setHiddenState = (direction = 1) => {
    killActiveTimeline();
    items.forEach(({ element, offsetY }) => {
      gsap.set(element, {
        autoAlpha: 0,
        y: offsetY * direction,
      });
    });
  };

  const setVisibleState = () => {
    killActiveTimeline();
    items.forEach(({ element }) => {
      gsap.set(element, {
        autoAlpha: 1,
        y: 0,
      });
    });
  };

  const playReveal = (direction = 1) => {
    killActiveTimeline();
    activeTimeline = gsap.timeline({
      defaults: {
        ease: "power3.out",
        overwrite: "auto",
      },
      onComplete: () => {
        activeTimeline = null;
      },
    });

    items.forEach(({ element, offsetY, duration, position }) => {
      activeTimeline.fromTo(
        element,
        {
          autoAlpha: 0,
          y: offsetY * direction,
        },
        {
          autoAlpha: 1,
          y: 0,
          duration,
        },
        position,
      );
    });
  };

  const playLeaveBack = () => {
    killActiveTimeline();
    activeTimeline = gsap.timeline({
      defaults: {
        ease: "power3.out",
        overwrite: "auto",
      },
      onComplete: () => {
        setHiddenState(1);
      },
    });

    activeTimeline
      .to(
        title,
        {
          autoAlpha: 0,
          y: 72,
          duration: 0.55,
        },
        0,
      )
      .to(
        text,
        {
          autoAlpha: 0,
          y: 72,
          duration: 0.55,
        },
        0.22,
      );
  };

  const syncState = (self) => {
    if (self.isActive) {
      setVisibleState();
      return;
    }

    if (self.progress >= 1) {
      setHiddenState(-1);
      return;
    }

    setHiddenState(1);
  };

  setHiddenState(1);

  const scrollTrigger = ScrollTrigger.create({
    trigger: title,
    start: "top 90%",
    endTrigger: section,
    end: "bottom top",
    invalidateOnRefresh: true,
    onRefresh: syncState,
    onEnter: () => playReveal(1),
    onEnterBack: () => playReveal(-1),
    onLeave: () => setHiddenState(-1),
    onLeaveBack: playLeaveBack,
  });

  requestAnimationFrame(() => {
    syncState(scrollTrigger);
  });
}

function createDirectionalReveal({
  trigger,
  items,
  start = "top 88%",
  endTrigger,
  end,
  onLeaveBackAnimation,
}) {
  const revealItems = items.filter((item) => item?.element);

  if (!trigger || !revealItems.length) {
    return;
  }

  let revealTimeline = null;
  let isVisible = false;

  const setHiddenState = (direction = 1) => {
    revealTimeline?.kill();
    revealTimeline = null;
    isVisible = false;

    revealItems.forEach(({ element, offsetY }) => {
      gsap.set(element, {
        autoAlpha: 0,
        y: offsetY * direction,
      });
    });
  };

  const setVisibleState = (visible) => {
    isVisible = visible;
  };

  const playReveal = (direction = 1) => {
    revealTimeline?.kill();
    revealItems.forEach(({ element }) => {
      gsap.killTweensOf(element);
    });

    isVisible = true;
    revealTimeline = gsap.timeline({
      defaults: {
        ease: "power3.out",
        overwrite: "auto",
      },
    });

    revealItems.forEach(
      ({ element, offsetY, duration = 0.95, position = 0 }) => {
        revealTimeline.fromTo(
          element,
          {
            autoAlpha: 0,
            y: offsetY * direction,
          },
          {
            autoAlpha: 1,
            y: 0,
            duration,
          },
          position,
        );
      },
    );
  };

  const revealIfNeeded = (direction = 1) => {
    if (isVisible) {
      return;
    }

    playReveal(direction);
  };

  const playLeaveBack = () => {
    revealTimeline?.kill();

    if (typeof onLeaveBackAnimation === "function") {
      revealTimeline =
        onLeaveBackAnimation({
          revealItems,
          setHiddenState,
          setVisibleState,
        }) || null;

      if (revealTimeline) {
        return;
      }
    }

    setHiddenState(1);
  };

  setHiddenState(1);

  const scrollTrigger = ScrollTrigger.create({
    trigger,
    start,
    endTrigger,
    end,
    invalidateOnRefresh: true,
    onRefreshInit: () => setHiddenState(1),
    onRefresh: (self) => {
      if (self.isActive) {
        revealIfNeeded(self.direction === -1 ? -1 : 1);
      }
    },
    onEnter: () => revealIfNeeded(1),
    onEnterBack: () => revealIfNeeded(-1),
    onLeave: () => setHiddenState(-1),
    onLeaveBack: playLeaveBack,
  });

  requestAnimationFrame(() => {
    if (scrollTrigger.isActive) {
      revealIfNeeded(scrollTrigger.direction === -1 ? -1 : 1);
    }
  });
}

const refreshScrollTrigger = () => {
  if (typeof ScrollTrigger !== "undefined") {
    ScrollTrigger.refresh();
  }
};

const runCleanupFns = (cleanupFns = []) => {
  cleanupFns
    .filter((cleanup) => typeof cleanup === "function")
    .reverse()
    .forEach((cleanup) => {
      cleanup();
    });
};

let storyAnimationsContext = null;
let storyAnimationsCleanup = [];
let storyAnimationsResizeTimerId = null;
let storyViewportWidth = window.innerWidth;
let storyViewportHeight = window.innerHeight;

const initializeStoryAnimations = () => {
  if (storyAnimationsResizeTimerId) {
    window.clearTimeout(storyAnimationsResizeTimerId);
    storyAnimationsResizeTimerId = null;
  }

  syncViewportMode();
  storyViewportWidth = window.innerWidth;
  storyViewportHeight = window.innerHeight;
  storyAnimationsContext?.revert();
  storyAnimationsContext = null;
  runCleanupFns(storyAnimationsCleanup);
  storyAnimationsCleanup = [];

  storyAnimationsContext = gsap.context(() => {
    if (!window.isMobile) {
      section1Pc();
      section2Pc();
      storyAnimationsCleanup.push(section3Pc());
      section5Pc();
      storyAnimationsCleanup.push(section7Pc());
      section8Pc();
      return;
    }

    section1Mb();
    section2Mb();
    section3Mb();
    section5Mb();
    section7Mb();
    section8Mb();
  });

  window.requestAnimationFrame(refreshScrollTrigger);
};

initializeStoryAnimations();

if (typeof STORY_MOBILE_MEDIA_QUERY.addEventListener === "function") {
  STORY_MOBILE_MEDIA_QUERY.addEventListener("change", initializeStoryAnimations);
} else if (typeof STORY_MOBILE_MEDIA_QUERY.addListener === "function") {
  STORY_MOBILE_MEDIA_QUERY.addListener(initializeStoryAnimations);
}

window.addEventListener("resize", () => {
  if (
    window.innerWidth === storyViewportWidth &&
    window.innerHeight === storyViewportHeight
  ) {
    return;
  }

  if (storyAnimationsResizeTimerId) {
    window.clearTimeout(storyAnimationsResizeTimerId);
  }

  storyAnimationsResizeTimerId = window.setTimeout(() => {
    storyAnimationsResizeTimerId = null;

    if (
      window.innerWidth === storyViewportWidth &&
      window.innerHeight === storyViewportHeight
    ) {
      return;
    }

    initializeStoryAnimations();
  }, 180);
});

if (document.readyState === "complete") {
  window.requestAnimationFrame(refreshScrollTrigger);
} else {
  window.addEventListener("load", refreshScrollTrigger, { once: true });
}
